import * as React from "react";

//WEBGL
import { Canvas } from "@react-three/fiber";
import { Html, Preload } from "@react-three/drei";
import { XR, useXR } from "@react-three/xr";

//TS
import { Hotspot, Scene, TourData } from "../../../Types.ts";

//GUI ELEMENTS
import ViewerWaypoint from "../TourViewerHotspots/exp-spots/ViewerWaypoint.tsx";
import ViewerLink from "../TourViewerHotspots/exp-spots/ViewerLink.tsx";
import ViewerHotspot from "../TourViewerHotspots/exp-spots/ViewerHotspot.tsx";
import { FullScreen } from "react-full-screen";
import ViewerQuiz from "../TourViewerHotspots/exp-spots/ViewerQuiz.tsx";

import XRSession from "../XR/XRSession.tsx";

import ExpOverlay from "./ExpOverlay";
import { UseAssetsQuery } from "../../../api/calls/Queries.ts";
import ViewerAudio from "../TourViewerHotspots/exp-spots/ViewerAudio.tsx";
import { useGetQuiz } from "../../../api/calls/Quiz.ts";
import LoadingExp from "../../common/LoadingExp.tsx";
import WebGLSphere from "../../common/WebGLSphere.tsx";
import {
  editorExperienceStore,
  useCurrentSceneStore,
  useSceneStore,
} from "../../../api/Zustand.ts";
import { useSidePanelStore } from "../TourViewerHeader.tsx";
import Camera from "../../common/Camera.tsx";
import Controls from "./Controls.tsx";
import { useMockEscapeRoomStore } from "../escape-room/zustand.ts";
import { useZoomStore } from "../../../state management/zustand/common-stores.ts";

function TourViewerExperience(props: {
  tourData: TourData;
  tourID: number;
  handle: any;
}) {
  const { tourData, tourID, handle } = props;

  // const [currentScene, setCurrentScene] = React.useState<Scene>();

  const { currentSceneID } = useCurrentSceneStore();

  const { setScene } = useSceneStore();

  const currentScene = React.useMemo(() => {
    return tourData.scenes.find((one) => one.id === currentSceneID);
  }, [currentSceneID, tourData.scenes]);

  React.useEffect(() => {
    if (currentScene) {
      setScene(currentScene);
    }
  }, [currentScene, setScene]);

  const { transition, setTransition } = editorExperienceStore();

  const { data: audioList } = UseAssetsQuery(tourID, 3);

  // React.useEffect(() => {
  //   setTransition(true);
  // }, [currentSceneID, setTransition]);

  const [zoom, setZoom] = React.useState<number>(
    parseInt(
      // @ts-expect-error
      tourData.scenes.find((one: Scene) => one.id === currentSceneID)?.fov
    ) || 75
  );

  const el = React.useRef<HTMLDivElement>(null);

  function preventDefault(e: WheelEvent) {
    e.preventDefault();
  }

  const sidePanel = useSidePanelStore();

  // window.addEventListener("mousemove", (e) => {
  //   if (el.current) {
  //     if (
  //       e.clientX > el.current.getBoundingClientRect().left &&
  //       e.clientX <
  //         el.current.getBoundingClientRect().left +
  //           el.current.getBoundingClientRect().width &&
  //       e.clientY > el.current.getBoundingClientRect().top &&
  //       e.clientY <
  //         el.current.getBoundingClientRect().top +
  //           el.current.getBoundingClientRect().height
  //     ) {
  //       window.addEventListener("wheel", preventDefault, {
  //         passive: false,
  //       });
  //     } else window.removeEventListener("wheel", preventDefault);
  //   }
  // });

  // const currentScene = React.useMemo(() => {

  //   return tourData.scenes.find((one) => one.id === currentSceneID)
  // }, [])

  const { setRequiredQuestions, whipeRequiredQuestions } =
    useMockEscapeRoomStore();

  React.useEffect(() => {
    if (currentScene?.hotspots) {
      whipeRequiredQuestions();

      const quizes = currentScene.hotspots.filter(
        (ones) => ones.type === "quiz"
      );
      quizes.forEach((quiz) => {
        return setRequiredQuestions(quiz.target as number, tourData);
      });
    }
  }, [
    currentScene?.hotspots,
    setRequiredQuestions,
    tourData,
    whipeRequiredQuestions,
  ]);

  const zoomHandling = React.useCallback(
    (e: React.WheelEvent) => {
      if (Math.sign(e.deltaY) === -1) {
        if (zoom <= 15) return;
        else setZoom(zoom - 4);
      } else {
        if (zoom >= 90) setZoom(90);
        else setZoom(zoom + 4);
      }
    },
    [zoom]
  );

  const a = useZoomStore();

  const [grabbing, setGrabbing] = React.useState<boolean>(false);

  return (
    <FullScreen handle={handle}>
      <ExpOverlay handle={handle} tourID={tourID} />
      {/* <EscapeRoomDebug /> */}

      {!currentScene ? (
        <div className="no-scene">
          Tour senza scene
          {currentSceneID}
          <span style={{ fontSize: "1rem" }}>Modifica nell'editor</span>
        </div>
      ) : (
        <>
          <div
            className="black-transition"
            id={transition ? undefined : "hidden"}
          >
            {transition && <LoadingExp />}
          </div>

          <div ref={el} className="transition-div">
            <Canvas
              flat
              linear
              dpr={1}
              onPointerDown={(e: React.PointerEvent) => {
                if (e.button === 0) {
                  setGrabbing(true);
                }
              }}
              onPointerUp={(e: React.PointerEvent) => {
                if (e.button === 0) {
                  setGrabbing(false);
                }
              }}
              onDoubleClick={() => sidePanel.setPanel(false)}
              style={{
                cursor: grabbing ? "grabbing" : "grab",
              }}
              onWheel={(e) => {
                zoomHandling(e);
                a.setZoom(e);
              }}
            >
              <XR referenceSpace="local" onInputSourcesChange={() => {}}>
                <Preload all />
                <Controls />
                <Camera />
                <ambientLight intensity={1} />

                {/* <TESTFLEX></TESTFLEX> */}
                {/* <TESTFLEX></TESTFLEX>
                <QUIZFLEXTEST
                  tourID={tourID}
                  userID={1}
                  hs={hs}
                  question={questionMock}
                ></QUIZFLEXTEST> */}

                <Hotspots tourData={tourData} tourID={tourID} />
                <React.Suspense fallback={<div>Error</div>}>
                  <WebGLSphere scene={currentScene} tourData={tourData} />
                </React.Suspense>

                {currentScene && (
                  <XRSession
                    currentScene={currentScene}
                    tourData={tourData}
                    transition={transition}
                  />
                )}
              </XR>
            </Canvas>
          </div>
        </>
      )}
    </FullScreen>
  );
}

const Hotspots = (props: { tourData: TourData; tourID: number }) => {
  const { tourData, tourID } = props;

  const { scene: currentScene } = useSceneStore();

  const { currentSceneID } = useCurrentSceneStore();

  const { zoom } = useZoomStore();

  const { data: quizData } = useGetQuiz(tourID, tourData.assignedQuizID);
  const { data: audioList } = UseAssetsQuery(tourID, 3);

  const { isPresenting } = useXR();

  if (isPresenting === false)
    return (
      <>
        {/* <Resize precise position={[0.4, 0, 0]}>
        <Svg
          src={"./svg/arrow.svg"}
          renderOrder={10}
          fillMeshProps={{ renderOrder: 2, position: [0, 0, -0.05] }}
          fillMaterial={
            new THREE.MeshBasicMaterial({
              color: "red",
              // depthTest: false,
              // transparent: true,
              // opacity: 0.5,
            })
          }
          strokeMeshProps={{ renderOrder: 1 }}
        ></Svg>
      </Resize> */}

        {currentScene?.hotspots.map((spot: Hotspot) => (
          <mesh position={[spot.x, spot.y, spot.z]} key={spot.id}>
            {spot.type === "transition" ? (
              <Html center>
                <ViewerWaypoint
                  waypoint={spot}
                  tourData={tourData}
                  zoom={zoom}
                />
              </Html>
            ) : spot.type === "link" ? (
              <Html center>
                <ViewerLink link={spot} zoom={zoom} />
              </Html>
            ) : spot.type === "popup" ? (
              <Html center>
                <ViewerHotspot popup={spot} tourData={tourData} zoom={zoom} />
              </Html>
            ) : spot.type === "quiz" ? (
              quizData?.data && (
                <Html center>
                  <ViewerQuiz
                    // @ts-expect-error
                    quizData={quizData}
                    quiz={spot}
                    tourData={tourData}
                    zoom={zoom}
                  />
                </Html>
              )
            ) : spot.type === "audio" ? (
              <ViewerAudio
                audioList={audioList}
                tourID={tourID}
                audio={spot}
                tourData={tourData}
                currentSceneID={currentSceneID!}
                zoom={zoom}
              />
            ) : null}
          </mesh>
        ))}
      </>
    );
};

export default TourViewerExperience;
